/* eslint-disable no-await-in-loop */

function getFilename(path: string) {
  const parts = path.split('/');
  return parts[parts.length - 1];
}

export function injectCSPMetaTagIntoHTML(html: string) {
  // HTML -> doc
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // remove any existing CSP meta tags
  const existing = doc.head.querySelectorAll('meta[http-equiv="Content-Security-Policy"]');
  if (existing.length) {
    for (let i = 0; i < existing.length; i += 1) {
      existing[i].remove();
    }
  }

  if (!doc.head) {
    throw new Error('index.html is missing <head> tag!');
  }

  // inject CSP meta tag
  doc.head.insertAdjacentHTML(
    'afterbegin',
    `
    <meta http-equiv="Content-Security-Policy" content="
    upgrade-insecure-requests;
    default-src
      'none';
    frame-src
      'self';
    child-src
      'self'
      blob:;
    script-src
      'self'
      'unsafe-inline'
      'unsafe-eval'
      blob:;
    style-src
      'self'
      'unsafe-inline';
    img-src
      'self'
      'unsafe-inline'
      data:
      blob:
      https://services.tzkt.io
      https://ipfs.infura.io
      https://*.infura-ipfs.io
      https://cloudflare-ipfs.com/
      https://ipfs.io/
      https://templewallet.com/logo.png
      https://gateway.pinata.cloud/;
    font-src
      'self'
      data:
      https://ipfs.infura.io
      https://*.infura-ipfs.io
      https://cloudflare-ipfs.com/
      https://fonts.googleapis.com/
      https://ipfs.io/
      https://gateway.pinata.cloud/;
    connect-src
      'self'
      https://better-call.dev
      https://*.better-call.dev
      https://*.cryptonomic-infra.tech
      https://cryptonomic-infra.tech
      https://*.infura.io
      https://*.infura-ipfs.io
      https://infura.io
      blob:
      data:
      ws:
      wss:
      bootstrap.libp2p.io
      preload.ipfs.io
      https://api.versum.xyz
      https://mainnet.smartpy.io
      https://mainnet-tezos.giganode.io
      https://api.etherscan.io
      https://api.thegraph.com
      https://*.tzkt.io
      https://api.hicdex.com
	    https://hdapi.teztools.io
      https://api.tzstats.com
      https://*.wikidata.org
      https://*.coinmarketcap.com
      https://api.openweathermap.org
      https://hicetnunc.xyz
      https://*.hicetnunc.xyz;
      https://api.teztok.com;
    manifest-src
      'self';
    base-uri
      'self';
    form-action
      'none';
    media-src
      'self'
      'unsafe-inline'
      data:
      blob:
      https://ipfs.infura.io
      https://*.infura-ipfs.io
      https://cloudflare-ipfs.com/
      https://ipfs.io/
      https://gateway.pinata.cloud/;
    prefetch-src
      'self'
      https://ipfs.infura.io
      https://*.infura-ipfs.io
      https://cloudflare-ipfs.com/
      https://fonts.googleapis.com/
      https://ipfs.io/
      https://gateway.pinata.cloud/;
    worker-src
      'self'
      'unsafe-inline'
      blob:;">
  `
  );

  // doc -> HTML
  return `<!DOCTYPE html><html>${doc.documentElement.innerHTML}</html>`;
}

export function injectCSPMetaTagIntoBuffer(buffer: Uint8Array) {
  const html = new TextDecoder().decode(buffer);
  const safeHTML = injectCSPMetaTagIntoHTML(html);
  return new TextEncoder().encode(safeHTML);
}

export async function validateWebDirectoryFiles(files: { [key: string]: Blob }) {
  // check for index.html file
  if (!files['index.html']) {
    return {
      valid: false,
      error: 'Missing index.html file',
    };
  }

  const pageBlob = files['index.html'];
  const htmlString = await pageBlob.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // check for <head> tag
  if (!doc.head) {
    return {
      valid: false,
      error: 'Missing <head> tag in index.html',
    };
  }

  return {
    valid: true,
  };
}

export async function prepareWebDirectory(files: Record<string, File>) {
  const entries = Object.entries(files);

  // Find root dir
  let rootDir: string | null = null;
  for (let i = 0; i < entries.length; i += 1) {
    const parts = entries[i][0].split('/');
    const filename = parts[parts.length - 1].toLowerCase();
    if (filename === 'index.html') {
      const partsClone = parts.slice();
      partsClone.pop();
      rootDir = partsClone.join('/');
      break;
    }
  }

  if (rootDir === null) {
    throw new Error('No index.html file found!');
  }

  // reconstruct with relative path keys
  const outFiles: Record<string, File> = {};
  entries.forEach(([path, file]) => {
    const relPath = path.replace(`${rootDir}/`, '');
    const filename = getFilename(relPath);
    outFiles[relPath] = new File([file], filename, { type: file.type });
  });

  const indexBlob = outFiles['index.html'];

  // inject CSP meta tag in all html files
  // eslint-disable-next-line no-restricted-syntax
  for (const k in outFiles) {
    if (k.endsWith('.html') || k.endsWith('.htm')) {
      const pageBuffer = new Uint8Array(await outFiles[k].arrayBuffer());
      const safePageBuffer = injectCSPMetaTagIntoBuffer(pageBuffer);
      const filename = getFilename(k);
      outFiles[k] = new File([safePageBuffer], filename, {
        type: indexBlob.type,
      });
    }
  }

  const check = await validateWebDirectoryFiles(outFiles);
  if (!check.valid) {
    throw new Error(check.error);
  }

  return outFiles;
}

function getCoverImagePathFromDoc(doc: Document) {
  let meta = doc.head.querySelector('meta[property="cover-image"]');
  if (!meta) {
    meta = doc.head.querySelector('meta[property="og:image"]');
  }

  if (!meta) return null;

  return meta.getAttribute('content');
}

export function injectCSPMetaTagIntoDataURI(dataURI: string) {
  // data URI -> HTML
  const prefix = 'data:text/html;base64,';
  const base64 = dataURI.replace(prefix, '');
  const html = atob(base64);

  // inject CSP meta tag
  const safeHTML = injectCSPMetaTagIntoHTML(html);

  // HTML -> data URI
  return `${prefix}${btoa(safeHTML)}`;
}

export function getCoverImagePathFromBuffer(buffer: Uint8Array) {
  // buffer -> html
  const html = new TextDecoder().decode(buffer);

  // html -> doc
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  return getCoverImagePathFromDoc(doc);
}
